import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiUserPlus, FiUserCheck, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';

const ClientCreditModal = ({ isOpen, onClose, onConfirm, clients = [] }) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fermer le dropdown si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Définir une date d'échéance par défaut à 30 jours
      const defaultDueDate = new Date();
      defaultDueDate.setDate(defaultDueDate.getDate() + 30);
      setDueDate(defaultDueDate.toISOString().split('T')[0]);
      setIsNewClient(false);
      setNewClientData({ name: '', phone: '' });
      setSearchTerm('');
      setSelectedClientId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (isNewClient) {
      if (!newClientData.name) {
        toast.warn("Veuillez entrer le nom du client.");
        return;
      }
      setLoading(true);
      try {
        const response = await api.post('/clients', newClientData);
        const newClient = response.data;
        toast.success(`Client "${newClient.name}" ajouté !`);
        onConfirm({ clientId: newClient._id, dueDate });
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Erreur lors de la création du client.";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    } else {
      if (selectedClientId) {
        onConfirm({ clientId: selectedClientId, dueDate });
      } else {
        toast.warn("Veuillez sélectionner un client.");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Vente à Crédit</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-600">{isNewClient ? "Ajouter un nouveau client" : "Sélectionner un client existant"}</p>
          <button 
            onClick={() => setIsNewClient(!isNewClient)} 
            className="text-brand-blue text-sm font-semibold flex items-center hover:underline"
          >
            {isNewClient ? <><FiUserCheck className="mr-1"/> Choisir existant</> : <><FiUserPlus className="mr-1"/> Nouveau client</>}
          </button>
        </div>

        {isNewClient ? (
          <div className="mb-4 space-y-3">
            <input 
              type="text" 
              placeholder="Nom du client" 
              value={newClientData.name} 
              onChange={(e) => setNewClientData({...newClientData, name: e.target.value})} 
              className="w-full p-2 border rounded"
              autoFocus
            />
            <input 
              type="tel" 
              placeholder="Téléphone (optionnel)" 
              value={newClientData.phone} 
              onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})} 
              className="w-full p-2 border rounded"
            />
          </div>
        ) : (
          <div className="mb-6 relative" ref={dropdownRef}>
            <div className="relative">
                <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setIsDropdownOpen(true); setSelectedClientId(''); }}
                    onFocus={() => setIsDropdownOpen(true)}
                    className={`w-full p-2 pl-9 border rounded ${!selectedClientId && searchTerm === '' ? 'border-gray-300' : selectedClientId ? 'border-green-500 bg-green-50' : 'border-brand-blue'}`}
                />
                {selectedClientId && <FiUserCheck className="absolute top-1/2 right-3 -translate-y-1/2 text-green-600" />}
            </div>
            
            {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-50">
                    {clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map(client => (
                        <div 
                            key={client._id} 
                            className="p-2 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-0"
                            onClick={() => {
                                setSelectedClientId(client._id);
                                setSearchTerm(client.name);
                                setIsDropdownOpen(false);
                            }}
                        >
                            <p className="font-bold text-gray-800">{client.name}</p>
                            {client.phone && <p className="text-xs text-gray-500">{client.phone}</p>}
                        </div>
                    ))}
                    {clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                        <div className="p-2 text-sm text-gray-500 text-center">Aucun client trouvé</div>
                    )}
                </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
          <input type="date" id="dueDate" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full p-2 border rounded bg-gray-50" />
        </div>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleConfirm} disabled={loading} className="bg-brand-blue text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50">
            {loading ? 'Traitement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientCreditModal;